import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button.js';
function useDialogA11y(open, containerRef, onCancel, onConfirm) {
    useEffect(() => {
        if (!open)
            return;
        const previouslyFocused = document.activeElement;
        // Move focus into the dialog so screen readers narrate the title and
        // keyboard users can Tab through buttons / inputs without leaving it.
        const first = containerRef.current?.querySelector('input, button, [tabindex]:not([tabindex="-1"])');
        first?.focus();
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
            }
            else if (e.key === 'Enter' && onConfirm) {
                // Don't hijack Enter when focus is in a textarea; for inputs the
                // form submit behavior (Enter = submit) is what we want.
                const target = e.target;
                if (target?.tagName === 'TEXTAREA')
                    return;
                // For ConfirmModal there's no input — Enter confirms.
                // For PromptModal the form's onSubmit handles Enter when focus
                // is in the input, so this branch only fires when focus is on
                // a button (which already triggers click) or the dialog itself.
                if (target?.tagName !== 'INPUT') {
                    e.preventDefault();
                    onConfirm();
                }
            }
        };
        document.addEventListener('keydown', onKey);
        // Prevent page scroll behind the overlay.
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
            previouslyFocused?.focus();
        };
    }, [open, containerRef, onCancel, onConfirm]);
}
export function ConfirmModal({ open, title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive = false, onConfirm, onCancel, }) {
    const containerRef = useRef(null);
    useDialogA11y(open, containerRef, onCancel, onConfirm);
    if (!open)
        return null;
    const variant = destructive ? 'danger' : 'primary';
    return createPortal(_jsx("div", { className: "kb-modal-overlay", onClick: onCancel, children: _jsxs("div", { ref: containerRef, className: "kb-modal", role: "alertdialog", "aria-modal": "true", "aria-labelledby": "kb-modal-title", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { id: "kb-modal-title", className: "kb-modal__title", children: title }), body !== undefined && _jsx("div", { className: "kb-modal__body", children: body }), _jsxs("div", { className: "kb-modal__actions", children: [_jsx(Button, { size: "sm", variant: "ghost", onClick: onCancel, children: cancelLabel }), _jsx(Button, { size: "sm", variant: variant, onClick: onConfirm, children: confirmLabel })] })] }) }), document.body);
}
export function PromptModal({ open, title, body, initialValue = '', placeholder = '', confirmLabel = 'Submit', cancelLabel = 'Cancel', destructive = false, onConfirm, onCancel, }) {
    const containerRef = useRef(null);
    const [value, setValue] = useState(initialValue);
    // Reset the input when the modal opens (re-opens reuse the same component).
    useEffect(() => {
        if (open)
            setValue(initialValue);
    }, [open, initialValue]);
    useDialogA11y(open, containerRef, onCancel);
    if (!open)
        return null;
    const variant = destructive ? 'danger' : 'primary';
    return createPortal(_jsx("div", { className: "kb-modal-overlay", onClick: onCancel, children: _jsxs("div", { ref: containerRef, className: "kb-modal", role: "dialog", "aria-modal": "true", "aria-labelledby": "kb-modal-title", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { id: "kb-modal-title", className: "kb-modal__title", children: title }), body !== undefined && _jsx("div", { className: "kb-modal__body", children: body }), _jsxs("form", { onSubmit: (e) => {
                        e.preventDefault();
                        onConfirm(value);
                    }, children: [_jsx("input", { type: "text", className: "kb-modal__input", value: value, placeholder: placeholder, onChange: (e) => setValue(e.currentTarget.value), autoFocus: true }), _jsxs("div", { className: "kb-modal__actions", children: [_jsx(Button, { size: "sm", variant: "ghost", type: "button", onClick: onCancel, children: cancelLabel }), _jsx(Button, { size: "sm", variant: variant, type: "submit", children: confirmLabel })] })] })] }) }), document.body);
}
//# sourceMappingURL=Modal.js.map